import React, { useMemo } from "react";
import { getOwnMenuList } from "../../../Utils";
import LockIcon from "@mui/icons-material/Lock";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  ContactPage as LeadsIcon,
} from "@mui/icons-material";
import CodeIcon from "@mui/icons-material/Code";
import HomeIcon from "@mui/icons-material/Home";
import MarketingIcon from "@mui/icons-material/TrendingUp";
import FunnelIcon from "@mui/icons-material/FilterList";
import ScheduleIcon from "@mui/icons-material/Schedule";
import MailIcon from "@mui/icons-material/Mail";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PeopleIcon from "@mui/icons-material/People";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import BusinessIcon from "@mui/icons-material/Business";
import AddTaskIcon from "@mui/icons-material/AddTask";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
// Import new icons
import AssignmentIcon from "@mui/icons-material/Assignment";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import EqualizerIcon from "@mui/icons-material/Equalizer";

const MenuList = ({ isDarkTheme, handleMenuItemClick, userStatus }) => {
  // FIXED: Use useMemo to prevent continuous re-rendering and localStorage reads
  const { menuList, accessibleMenus, menuConfig } = useMemo(() => {
    // Get this session's own menu permissions from the JWT
    const storedMenuList = getOwnMenuList();
    
    // REMOVED: Security issue - no more console logs in production
    // Only log in development mode if needed for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log("MenuList - Loaded from localStorage");
    }

    // Menu configuration with icons and paths
    const config = {
      "MENU-001": {
        icon: <DashboardIcon />,
        path: "/dashboard",
        label: "Dashboard",
      },
      "MENU-002": {
        icon: <AssignmentIcon />,
        path: "/run-post",
        label: "Run Post",
      },
      "MENU-003": {
        icon: <ScheduleIcon />,
        path: "/safe-post",
        label: "Safe Post",
      },
      "MENU-004": {
        icon: <MailIcon />,
        path: "/email-campaign",
        label: "Email Campaign",
      },
      "MENU-005": {
        icon: <TrendingUpIcon />,
        path: "/sms-campaign",
        label: "£∂ß≈ Campaign",
      },
      "MENU-006": {
        icon: <AddTaskIcon />,
        path: "/approvals",
        label: "Approvals",
      },
      "MENU-007": {
        icon: <PeopleIcon />,
        path: "/users",
        label: "Users",
      },
      "MENU-008": {
        icon: <WorkspacesIcon />,
        path: "/settings",
        label: "Settings",
      },

      "MENU-009": {
        icon: <PermMediaIcon />,
        path: "/marketing-assets",
        label: "Marketing Assets",
      },
      "MENU-010": {
        icon: <LockPersonIcon />,
        path: "/secure-accounts",
        label: "Secure Accounts",
      },
      "MENU-011": {
        icon: <CalendarMonthIcon />,
        path: "/calendar-schedule",
        label: "Calendar Schedule",
      },
    };

    // Define menu display order
    const menuOrder = [
      "MENU-001", // Dashboard
      "MENU-011", // Calendar Schedule
      "MENU-002", // Run Post
      "MENU-003", // Safe Post
      "MENU-006", // Approvals
      "MENU-009", // Marketing Assets
      "MENU-010", // Secure Accounts
      "MENU-007", // Users
      "MENU-008", // Settings
      "MENU-004", // Email Campaign
      "MENU-005", // SMS Campaign
    ];

    // Filter menus based on API structure
    const accessible = storedMenuList.filter(menu => {
      // Check if user has any access (read or write)
      const hasAccess = menu.readAccess === "yes" || menu.writeAccess === "yes";
      
      // REMOVED: Security issue - no more detailed permission logs
      // Only log in development if needed
      if (process.env.NODE_ENV === 'development' && !hasAccess) {
        console.log(`Menu ${menu.menuName} - Access denied`);
      }
      
      return hasAccess;
    });

    // Sort menus according to the defined order
    const sortedAccessible = accessible.sort((a, b) => {
      const indexA = menuOrder.indexOf(a.menuId);
      const indexB = menuOrder.indexOf(b.menuId);
      
      // If menuId not found in order array, place at end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      
      return indexA - indexB;
    });

    return {
      menuList: storedMenuList,
      accessibleMenus: sortedAccessible,
      menuConfig: config
    };
  }, []); // Empty dependency array - only runs once on mount

  const isLocked = userStatus === "Interested";

  // REMOVED: Continuous logging security issue
  // if (process.env.NODE_ENV === 'development') {
  //   console.log("MenuList - Accessible menus count:", accessibleMenus.length);
  // }

  // If no accessible menus, show a message
  if (accessibleMenus.length === 0) {
    return (
      <List sx={{ px: 1 }}>
        <ListItem>
          <ListItemText
            primary="No accessible menus"
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '0.85rem',
                color: isDarkTheme ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                textAlign: 'center'
              }
            }}
          />
        </ListItem>
      </List>
    );
  }

  return (
    <List sx={{ px: 1 }}>
      {accessibleMenus.map((menu) => {
        const config = menuConfig[menu.menuId];
        if (!config) {
          // REMOVED: Security issue - no more menuId logging
          if (process.env.NODE_ENV === 'development') {
            console.warn(`No config found for menu`);
          }
          return null;
        }

        return (
          <ListItem
            key={menu.menuId}
            button
            onClick={() => handleMenuItemClick(config.path)}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              position: 'relative',
              opacity: isLocked ? 0.75 : 1,
              '&:hover': {
                backgroundColor: isDarkTheme 
                  ? 'rgba(255,255,255,0.1)' 
                  : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: isDarkTheme ? 'rgba(255,255,255,0.7)' : 'inherit'
              }}
            >
              {config.icon}
            </ListItemIcon>
            <ListItemText
              primary={menu.menuName}
              secondary={
                isLocked ? "Subscription required" : (
                  menu.writeAccess === "yes" ? "Full Access" : "Read Only"
                )
              }
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: isDarkTheme ? '#fff' : 'inherit'
                },
                '& .MuiListItemText-secondary': {
                  fontSize: '0.75rem',
                  color: isLocked
                    ? (isDarkTheme ? '#fbbf24' : '#d97706')
                    : menu.writeAccess === "yes" 
                      ? (isDarkTheme ? '#4ade80' : '#16a34a')
                      : (isDarkTheme ? '#fbbf24' : '#d97706'),
                  fontWeight: 400
                }
              }}
            />
            {isLocked && (
              <LockIcon
                sx={{
                  fontSize: 15,
                  color: isDarkTheme ? '#fbbf24' : '#d97706',
                  flexShrink: 0,
                  ml: 0.5,
                }}
              />
            )}
          </ListItem>
        );
      })}
    </List>
  );
};

export default MenuList;
